/**
 * CardAssigneePopover — multi-select checklist of board members for
 * assigning/unassigning members to a card. Uses Radix UI Popover + Checkbox
 * with Tailwind CSS styling.
 *
 * Supports:
 * - Searchable member list (filter by displayName or email)
 * - Keyboard navigation (type to filter, arrow keys, Space to toggle, Escape to close)
 * - Optimistic updates with revert on failure
 * - Read-only mode for users with read-only board role
 *
 * Validates: Requirements 15.1, 15.2, 15.3, 15.4, 15.5, 15.6, 15.7, 15.8
 */

import * as React from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import * as api from '../../services/api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BoardMember {
  readonly userId: string;
  readonly displayName: string;
  readonly email: string;
}

export interface CardAssignee {
  readonly userId: string;
  readonly displayName: string;
}

export interface CardAssigneePopoverProps {
  readonly boardId: string;
  readonly cardId: string;
  readonly boardMembers: readonly BoardMember[];
  readonly currentAssignees: readonly CardAssignee[];
  readonly isReadOnly: boolean;
  readonly onAssignmentChange: (assignees: CardAssignee[]) => void;
  readonly onAvatarsUpdated?: (avatarUrls: string[]) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function filterMembers(
  members: readonly BoardMember[],
  query: string,
): BoardMember[] {
  if (!query.trim()) return [...members];
  const lower = query.toLowerCase().trim();
  return members.filter(
    (m) =>
      m.displayName.toLowerCase().includes(lower) ||
      m.email.toLowerCase().includes(lower),
  );
}

function isAssigned(
  userId: string,
  assignees: readonly CardAssignee[],
): boolean {
  return assignees.some((a) => a.userId === userId);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CardAssigneePopover({
  boardId,
  cardId,
  boardMembers,
  currentAssignees,
  isReadOnly,
  onAssignmentChange,
  onAvatarsUpdated,
}: CardAssigneePopoverProps): React.ReactElement {
  const [isOpen, setIsOpen] = React.useState(false);
  const [filterQuery, setFilterQuery] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const [error, setError] = React.useState<string | null>(null);
  const [isPending, setIsPending] = React.useState(false);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);

  const filteredMembers = React.useMemo(
    () => filterMembers(boardMembers, filterQuery),
    [boardMembers, filterQuery],
  );

  // Reset state when popover opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setFilterQuery('');
      setActiveIndex(-1);
      setError(null);
      // Focus input when opened
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [isOpen]);

  // Scroll active item into view
  React.useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll<HTMLElement>('[role="option"]');
    const activeItem = items[activeIndex];
    if (activeItem && typeof activeItem.scrollIntoView === 'function') {
      activeItem.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  // Reset active index when filter changes
  React.useEffect(() => {
    setActiveIndex(-1);
  }, [filterQuery]);

  // ---------------------------
  // Toggle assignment
  // ---------------------------

  const handleToggle = React.useCallback(
    async (member: BoardMember) => {
      if (isPending) return;

      const previousAssignees = [...currentAssignees];
      const wasAssigned = isAssigned(member.userId, currentAssignees);

      // Compute optimistic new assignees
      let newAssignees: CardAssignee[];
      if (wasAssigned) {
        newAssignees = currentAssignees.filter(
          (a) => a.userId !== member.userId,
        );
      } else {
        newAssignees = [
          ...currentAssignees,
          { userId: member.userId, displayName: member.displayName },
        ];
      }

      // Optimistic update
      onAssignmentChange(newAssignees);
      setError(null);
      setIsPending(true);

      try {
        const response = await api.put<{ data: { memberAvatars?: string[] } }>(`/v1/boards/${boardId}/cards/${cardId}`, {
          members: newAssignees.map((a) => ({
            userId: a.userId,
            displayName: a.displayName,
          })),
        });
        // Update board-level avatars with presigned URLs from the response
        if (onAvatarsUpdated && response.data?.memberAvatars) {
          onAvatarsUpdated(response.data.memberAvatars);
        }
      } catch {
        // Revert on failure
        onAssignmentChange(previousAssignees);
        setError('Failed to update assignees. Please try again.');
      } finally {
        setIsPending(false);
      }
    },
    [boardId, cardId, currentAssignees, isPending, onAssignmentChange],
  );

  // ---------------------------
  // Keyboard navigation
  // ---------------------------

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const itemCount = filteredMembers.length;
      if (itemCount === 0) return;

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          setActiveIndex((prev) =>
            prev < itemCount - 1 ? prev + 1 : 0,
          );
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          setActiveIndex((prev) =>
            prev > 0 ? prev - 1 : itemCount - 1,
          );
          break;
        }
        case ' ': {
          // Space toggles the active item only if we have an active index
          if (activeIndex >= 0 && activeIndex < itemCount) {
            event.preventDefault();
            const member = filteredMembers[activeIndex];
            if (member) {
              handleToggle(member);
            }
          }
          break;
        }
        case 'Escape': {
          event.preventDefault();
          setIsOpen(false);
          break;
        }
      }
    },
    [activeIndex, filteredMembers, handleToggle],
  );

  // Read-only mode: just render nothing (parent handles display)
  if (isReadOnly) {
    return <></>;
  }

  // ---------------------------
  // Render: Editable with popover (just the Add button + dropdown)
  // ---------------------------

  const listboxId = 'card-assignee-listbox';

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs"
          aria-label="Add or remove card assignees"
        >
          {currentAssignees.length === 0 ? 'Assign' : 'Change'}
        </Button>
      </Popover.Trigger>

          <Popover.Portal>
            <Popover.Content
              sideOffset={4}
              align="start"
              className={cn(
                'z-50 w-64 overflow-hidden rounded-md border',
                'bg-popover text-popover-foreground shadow-md',
                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
              )}
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              {/* Search input */}
              <div className="border-b px-3 py-2">
                <input
                  ref={inputRef}
                  type="text"
                  role="combobox"
                  aria-expanded={true}
                  aria-controls={listboxId}
                  aria-activedescendant={
                    activeIndex >= 0
                      ? `assignee-option-${activeIndex}`
                      : undefined
                  }
                  aria-autocomplete="list"
                  aria-label="Filter members"
                  placeholder="Filter members…"
                  value={filterQuery}
                  onChange={(e) => setFilterQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className={cn(
                    'w-full rounded-sm border border-input bg-background px-2 py-1.5 text-sm',
                    'placeholder:text-muted-foreground',
                    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                  )}
                />
              </div>

              {/* Error message */}
              {error && (
                <div
                  role="alert"
                  className="border-b px-3 py-2 text-xs text-destructive"
                >
                  {error}
                </div>
              )}

              {/* Member checklist */}
              <ul
                id={listboxId}
                ref={listRef}
                role="listbox"
                aria-label="Board members"
                aria-multiselectable="true"
                className="max-h-60 overflow-y-auto p-1"
              >
                {filteredMembers.length === 0 ? (
                  <li className="px-3 py-2 text-xs text-muted-foreground">
                    No members found
                  </li>
                ) : (
                  filteredMembers.map((member, index) => {
                    const checked = isAssigned(member.userId, currentAssignees);
                    const isActive = index === activeIndex;

                    return (
                      <li
                        key={member.userId}
                        id={`assignee-option-${index}`}
                        role="option"
                        aria-selected={checked}
                        className={cn(
                          'flex cursor-pointer select-none items-center gap-2 rounded-sm px-3 py-2 text-sm',
                          'transition-colors',
                          isActive
                            ? 'bg-accent text-accent-foreground'
                            : 'hover:bg-accent hover:text-accent-foreground',
                        )}
                        onMouseEnter={() => setActiveIndex(index)}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleToggle(member)}
                      >
                        <Checkbox
                          checked={checked}
                          tabIndex={-1}
                          aria-hidden="true"
                          className="pointer-events-none"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">
                            {member.displayName}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            {member.email}
                          </p>
                        </div>
                      </li>
                    );
                  })
                )}
              </ul>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
  );
}
